/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import "./ImageSlider.scss";
// material ui
// localizations
import { withTranslation, useTranslation } from "react-i18next";
import { useMediaQuery } from "@material-ui/core";

function ImageSlider(props) {
  const { t } = useTranslation();
  const appState = useSelector((state) => state);
  
  const {
    images = [],
    autoPlay = false,
    autoPlayInterval = 3000,
    showDots = true,
    showThumbnails = true,
    className = "",
    onImageClick = null
  } = props;

  // Ensure autoPlay is always false
  const isAutoPlayDisabled = true;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [slideDirection, setSlideDirection] = useState('next');
  const intervalRef = useRef(null);
  const min768 = useMediaQuery("(min-width:768px)");

  // Auto-play functionality - DISABLED
  // No auto-play logic - images only change on user interaction

  // Debug: Monitor currentIndex changes
  useEffect(() => {
    console.log('Current index changed to:', currentIndex);
  }, [currentIndex]);


  const handleImageClick = () => {
    if (onImageClick) {
      onImageClick(images[currentIndex], currentIndex);
    }
  };

  // Image preloading and lazy loading
  useEffect(() => {
    const preloadImages = async () => {
      setIsLoading(true);
      const loadPromises = images.map((image, index) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            setLoadedImages(prev => new Set([...prev, index]));
            resolve();
          };
          img.onerror = () => {
            setLoadedImages(prev => new Set([...prev, index]));
            resolve();
          };
          img.src = typeof image === 'string' ? image : image.url;
        });
      });
      
      await Promise.all(loadPromises);
      setIsLoading(false);
    };

    if (images.length > 0) {
      preloadImages();
    }
  }, [images]);

  // Touch/Swipe functionality - Fixed version
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    e.preventDefault();
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    e.preventDefault();
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = (e) => {
    e.preventDefault();
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && images.length > 1 && !isTransitioning) {
      // Swipe left - go to next image
      goToNext();
    }
    if (isRightSwipe && images.length > 1 && !isTransitioning) {
      // Swipe right - go to previous image
      goToPrevious();
    }
    
    // Reset touch states
    setTouchStart(null);
    setTouchEnd(null);
  };

  // Enhanced navigation with slide animation
  const goToSlide = (index) => {
    if (isTransitioning) return;
    console.log('Manual navigation to slide:', index);
    
    const direction = index > currentIndex ? 'next' : 'prev';
    setSlideDirection(direction);
    setIsTransitioning(true);
    
    // Start slide animation
    setTimeout(() => {
      setCurrentIndex(index);
      // End animation after transition completes
      setTimeout(() => setIsTransitioning(false), 400);
    }, 10);
  };

  const goToPrevious = () => {
    if (isTransitioning) return;
    setSlideDirection('prev');
    setIsTransitioning(true);
    
    setTimeout(() => {
      setCurrentIndex(currentIndex === 0 ? images.length - 1 : currentIndex - 1);
      setTimeout(() => setIsTransitioning(false), 400);
    }, 10);
  };

  const goToNext = () => {
    if (isTransitioning) return;
    setSlideDirection('next');
    setIsTransitioning(true);
    
    setTimeout(() => {
      setCurrentIndex(currentIndex === images.length - 1 ? 0 : currentIndex + 1);
      setTimeout(() => setIsTransitioning(false), 400);
    }, 10);
  };

  if (!images || images.length === 0) {
    return <div className="image-slider-empty">No images available</div>;
  }

  return (
    <div 
      className={`image-slider ${className}`}
      // onMouseEnter={() => setIsHovered(true)}
      // onMouseLeave={() => setIsHovered(false)}
    >
      {/* Main Image Display */}
      <div className="main-image-container">
        <div 
          className={`main-image-wrapper ${isTransitioning ? `sliding-${slideDirection}` : ''}`}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {isLoading && !loadedImages.has(currentIndex) ? (
            <div className="image-loading">
              <div className="loading-spinner"></div>
              <p>Loading image...</p>
            </div>
          ) : (
            <div className="image-container">
              <img
                src={images[currentIndex]?.url || images[currentIndex]}
                alt={`Slide ${currentIndex + 1}`}
                className={`main-image ${loadedImages.has(currentIndex) ? 'loaded' : 'loading'}`}
                onClick={handleImageClick}
                loading="lazy"
                onLoad={() => setLoadedImages(prev => new Set([...prev, currentIndex]))}
              />
            </div>
          )}
          
          {/* Navigation Arrows */}
          {(images.length > 1 && min768) && (
            <>
              <button 
                className="nav-arrow nav-arrow-left" 
                onClick={goToPrevious}
                aria-label="Previous image"
              >
                &#8249;
              </button>
              <button 
                className="nav-arrow nav-arrow-right" 
                onClick={goToNext}
                aria-label="Next image"
              >
                &#8250;
              </button>
            </>
          )}
        </div>

        {/* Navigation Dots */}
        {showDots && images.length > 1 && (
          <div className="dots-container">
            {images.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
        </div>

        {/* Thumbnail Navigation */}
      {showThumbnails && images.length > 1 && (
        <div className="thumbnails-container">
          {images.map((image, index) => (
            <div
              key={index}
              className={`thumbnail-wrapper ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
            >
              <img
                src={typeof image === 'string' ? image : image.url}
                alt={`Thumbnail ${index + 1}`}
                className={`thumbnail-image ${loadedImages.has(index) ? 'loaded' : 'loading'}`}
                loading="lazy"
                onLoad={() => setLoadedImages(prev => new Set([...prev, index]))}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default withTranslation()(ImageSlider);